import { ComponentPropsWithRef, forwardRef } from "react";
import clsx from 'clsx';
import ButtonStyle from "./css/Button.module.css";
import BoxShadowProps from "./types/ui/boxShadow";
import BorderProps from "./types/ui/border";
import transWidthHeight from "./utils/transWidthHeight";
import colorToRgba from "../utils/colorToRgba";
type ButtonProps = ComponentPropsWithRef<"button"> & BoxShadowProps & BorderProps & {
    variant?: "primary" | "black" | "white" | "gray" | "regist" | "insert" | "update" | "delete" | "cancel" | "destructive" | "info" | "success" | "warning" | "error";
    shape?: "square" | "round" | "pill" | "circle" ;
    fullWidth?: boolean;
    width?: number | string;
    height?: number | string;
    icon?: React.ReactNode;
    iconPosition?: "left" | "right";
    padding?: number | string;
    paddingX?: number | string;
    paddingY?: number | string;
    paddingTop?: number | string;
    paddingBottom?: number | string;
    paddingLeft?: number | string;
    paddingRight?: number | string;
    borderColor?: string;
    borderRadius?: number;
    color?: string;
    backgroundColor?: string;
    isLoading?: boolean;
    isLock?: boolean;
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
    children,
    border,
    color,
    borderRadius = 8,
    variant,
    shape,
    backgroundColor,
    fullWidth = false,
    width = "auto",
    height = "auto",
    shadow = 0,
    borderColor = "rgba(221, 221, 221, 1)",
    shadowDirection = "default",
    icon,
    iconPosition = "left",
    padding, paddingX, paddingY,
    paddingTop, paddingBottom, paddingLeft, paddingRight,
    isLoading = false,
    isLock = false,
    style,
    className,
    disabled,
    ...props
}, ref) => {
    // white variant일 때는 border 기본값 1, 다른 variant는 기본값 0
    const defaultBorder = variant === "white" ? 1 : 0;
    const borderWidth = border !== undefined ? border : defaultBorder;
    
    const boxShadowValue = shadow ? `${shadowDirection === "default" ? `0 0 0 ${shadow}px rgba(0, 0, 0, 0.14)` : `0 4px`} ${shadow}px 0px rgba(0, 0, 0, 0.14)` : "none";
    const borderValue = borderWidth ? `${borderWidth}px solid ${borderColor}` : "none";
    const widthValue = transWidthHeight(width);
    const heightValue = transWidthHeight(height);
    const backgroundColorValue = backgroundColor ? colorToRgba(backgroundColor) : undefined;
    return (
        <button 
            className={clsx(
                ButtonStyle.button,
                !backgroundColor && variant && ButtonStyle[variant],
                shape && ButtonStyle[shape],
                ButtonStyle[`icon-${iconPosition}`],
                fullWidth && ButtonStyle.fullWidth,
                isLoading && ButtonStyle.loading,
                isLock && ButtonStyle.isLock,
            )}
            style={{
                color: color,
                boxShadow: boxShadowValue,
                border: borderValue,
                borderRadius: borderRadius ? `${borderRadius}px` : 0,
                width: fullWidth ? "100%" : widthValue,
                height: heightValue,
                paddingLeft: transWidthHeight(paddingLeft) ?? transWidthHeight(paddingX) ?? transWidthHeight(padding),
                paddingRight: transWidthHeight(paddingRight) ?? transWidthHeight(paddingX) ?? transWidthHeight(padding),
                paddingTop: transWidthHeight(paddingTop) ?? transWidthHeight(paddingY) ?? transWidthHeight(padding),
                paddingBottom: transWidthHeight(paddingBottom) ?? transWidthHeight(paddingY) ?? transWidthHeight(padding),
                backgroundColor: backgroundColor && backgroundColorValue,
                ...style,
            }}
            ref={ref}
            disabled={disabled}
            {...props}
        >
            {isLoading ? (
                <>
                    <span className={ButtonStyle.spinner}></span>
                    {children}
                </>
            ) : (
                <>
                    {icon}
                    {children}
                </>
            )}
        </button>
    )
});

Button.displayName = "Button";

export default Button;
export type { ButtonProps };